import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import Header from "../components/Header";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { ArrowLeft, Copy, Download, Share2, Loader2, Check, ChevronDown, Globe2, Clock, Languages } from "lucide-react";
import { toast } from "sonner";

export default function TranscriptView() {
    const { id } = useParams();
    const { user, loading } = useAuth();
    const nav = useNavigate();
    const [t, setT] = useState(null);
    const [fetching, setFetching] = useState(true);
    const [copied, setCopied] = useState(false);
    const [dlOpen, setDlOpen] = useState(false);
    const [trOpen, setTrOpen] = useState(false);
    const [translating, setTranslating] = useState(false);
    const [activeLang, setActiveLang] = useState(null);

    useEffect(() => {
        if (!loading && !user) nav("/auth");
    }, [loading, user, nav]);

    useEffect(() => {
        if (!user) return;
        (async () => {
            try {
                const res = await api.get(`/transcripts/${id}`);
                setT(res.data);
            } catch (e) {
                toast.error("Failed to load transcript");
                nav("/app");
            } finally {
                setFetching(false);
            }
        })();
    }, [id, user, nav]);

    const copyAll = async () => {
        if (!t) return;
        const text = t.segments.map((s) => `[${formatTime(s.start)}] ${s.speaker || "Speaker"}: ${s.text}`).join("\n\n");
        await navigator.clipboard.writeText(text);
        setCopied(true);
        toast.success("Copied to clipboard");
        setTimeout(() => setCopied(false), 1500);
    };

    const download = (format) => {
        if (!t) return;
        let content = "";
        let mime = "text/plain";
        let ext = "txt";

        if (format === "txt") {
            content = t.segments.map((s) => `[${formatTime(s.start)}] ${s.speaker || "Speaker"}: ${s.text}`).join("\n\n");
        } else if (format === "srt") {
            content = t.segments
                .map((s, i) => `${i + 1}\n${srtTime(s.start)} --> ${srtTime(s.end)}\n${(s.speaker ? s.speaker + ": " : "") + s.text}\n`)
                .join("\n");
            ext = "srt";
        } else if (format === "json") {
            content = JSON.stringify(t, null, 2);
            mime = "application/json";
            ext = "json";
        }

        const blob = new Blob([content], { type: mime });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${(t.source_name || "transcript").replace(/\.[^/.]+$/, "")}.${ext}`;
        a.click();
        URL.revokeObjectURL(url);
        setDlOpen(false);
    };

    const share = async () => {
        try {
            const res = await api.post(`/transcripts/${id}/share`);
            const isPublic = res.data.is_public;
            setT((prev) => ({ ...prev, is_public: isPublic }));
            if (isPublic) {
                const url = `${window.location.origin}/share/${id}`;
                await navigator.clipboard.writeText(url);
                toast.success("Share link copied", { description: url });
            } else {
                toast.success("Sharing disabled");
            }
        } catch {
            toast.error("Could not toggle sharing");
        }
    };

    const translate = async (lang) => {
        setTranslating(true);
        setTrOpen(false);
        try {
            const res = await api.post(`/transcripts/${id}/translate`, { target_language: lang });
            const newT = { ...t, translations: { ...(t.translations || {}), [lang]: res.data.translation } };
            setT(newT);
            setActiveLang(lang);
            toast.success(`Translated to ${lang}`);
        } catch (e) {
            toast.error("Translation failed", { description: e?.response?.data?.detail || e.message });
        } finally {
            setTranslating(false);
        }
    };

    if (loading || fetching) {
        return (
            <div className="min-h-screen bg-white">
                <Header />
                <div className="flex items-center justify-center py-32 text-slate-400">
                    <Loader2 className="w-6 h-6 animate-spin" />
                </div>
            </div>
        );
    }
    if (!t) return null;

    return (
        <div className="min-h-screen bg-white">
            <Header />
            <main className="max-w-3xl mx-auto px-6 py-10 sm:py-14">
                <Link to="/app" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 transition-colors mb-6" data-testid="back-link">
                    <ArrowLeft className="w-4 h-4" strokeWidth={1.75} /> Back to dashboard
                </Link>

                <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 break-all" data-testid="transcript-title">
                    {t.source_name}
                </h1>
                <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-slate-500">
                    {t.language && (
                        <span className="inline-flex items-center gap-1.5"><Globe2 className="w-3.5 h-3.5" strokeWidth={1.75} />{t.language}</span>
                    )}
                    {t.duration_seconds > 0 && (
                        <span className="inline-flex items-center gap-1.5 font-mono"><Clock className="w-3.5 h-3.5" strokeWidth={1.75} />{formatTime(t.duration_seconds)}</span>
                    )}
                    <span>·</span>
                    <span>{new Date(t.created_at).toLocaleString()}</span>
                </div>

                {/* Actions */}
                <div className="mt-6 flex flex-wrap items-center gap-2">
                    <button
                        onClick={copyAll}
                        className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                        data-testid="copy-btn"
                    >
                        {copied ? <Check className="w-4 h-4 text-emerald-600" strokeWidth={2} /> : <Copy className="w-4 h-4" strokeWidth={1.75} />}
                        {copied ? "Copied" : "Copy"}
                    </button>

                    <div className="relative">
                        <button
                            onClick={() => setDlOpen((o) => !o)}
                            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
                            data-testid="download-btn"
                        >
                            <Download className="w-4 h-4" strokeWidth={1.75} /> Download <ChevronDown className="w-3.5 h-3.5" strokeWidth={1.75} />
                        </button>
                        {dlOpen && (
                            <div className="absolute left-0 mt-1 w-32 rounded-xl border border-slate-200 bg-white shadow-lg overflow-hidden z-10" data-testid="download-menu">
                                {["txt", "srt", "json"].map((f) => (
                                    <button
                                        key={f}
                                        onClick={() => download(f)}
                                        className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 text-slate-700"
                                        data-testid={`download-${f}`}
                                    >
                                        Download .{f}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <button
                        onClick={share}
                        className={`inline-flex items-center gap-1.5 rounded-xl border px-3.5 py-2 text-sm font-medium transition-colors ${
                            t.is_public ? "border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                        }`}
                        data-testid="share-btn"
                    >
                        <Share2 className="w-4 h-4" strokeWidth={1.75} />
                        {t.is_public ? "Sharing on" : "Share"}
                    </button>

                    <div className="relative">
                        <button
                            onClick={() => setTrOpen((o) => !o)}
                            disabled={translating || t.status !== "done"}
                            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50"
                            data-testid="translate-btn"
                        >
                            {translating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Languages className="w-4 h-4" strokeWidth={1.75} />}
                            Translate
                            <ChevronDown className="w-3.5 h-3.5" strokeWidth={1.75} />
                        </button>
                        {trOpen && (
                            <div className="absolute right-0 mt-1 w-48 max-h-72 overflow-auto rounded-xl border border-slate-200 bg-white shadow-lg z-10" data-testid="translate-menu">
                                {["Spanish","French","German","Hindi","Mandarin","Arabic","Portuguese","Japanese","Korean","Italian","Russian","Dutch","English"].map((lang) => (
                                    <button
                                        key={lang}
                                        onClick={() => translate(lang)}
                                        className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 text-slate-700"
                                        data-testid={`translate-to-${lang.toLowerCase()}`}
                                    >
                                        {lang}{t.translations?.[lang] ? " ✓" : ""}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Status banners */}
                {t.status === "processing" && (
                    <div className="mt-8 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-center gap-2" data-testid="processing-banner">
                        <Loader2 className="w-4 h-4 animate-spin" /> Processing... refresh in a moment.
                    </div>
                )}
                {t.status === "error" && (
                    <div className="mt-8 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" data-testid="error-banner">
                        {t.error || "Transcription failed."}
                    </div>
                )}

                {/* Active translation panel */}
                {activeLang && t.translations?.[activeLang] && (
                    <div className="mt-10 rounded-2xl border border-indigo-100 bg-indigo-50/40 p-6" data-testid="translation-panel">
                        <div className="flex items-center justify-between mb-3">
                            <div className="inline-flex items-center gap-2 text-xs font-medium text-indigo-700">
                                <Languages className="w-3.5 h-3.5" strokeWidth={2} />
                                Translation · {activeLang}
                            </div>
                            <button
                                onClick={() => setActiveLang(null)}
                                className="text-xs text-indigo-700/70 hover:text-indigo-700"
                                data-testid="close-translation"
                            >
                                Show original
                            </button>
                        </div>
                        <p className="text-[15px] leading-relaxed text-slate-800 whitespace-pre-wrap">
                            {t.translations[activeLang]}
                        </p>
                    </div>
                )}

                {/* Transcript body */}
                {t.status === "done" && !activeLang && (
                    <div className="mt-10 space-y-1" data-testid="transcript-body">
                        {t.segments.map((s, i) => (
                            <div key={i} className="group flex gap-4 p-4 rounded-xl hover:bg-slate-50 transition-colors" data-testid={`segment-${i}`}>
                                <div className="shrink-0 w-24 pt-0.5">
                                    <div className="text-sm font-medium text-indigo-600 truncate">{prettySpeaker(s.speaker)}</div>
                                    <div className="text-xs font-mono text-slate-400 mt-0.5">{formatTime(s.start)}</div>
                                </div>
                                <p className="flex-1 text-[15px] leading-relaxed text-slate-800 selection:bg-indigo-100">
                                    {s.text}
                                </p>
                            </div>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
}

function prettySpeaker(s) {
    if (!s) return "Speaker";
    const m = s.match(/(\d+)/);
    if (m) return `Speaker ${parseInt(m[1], 10) + 1}`;
    return s;
}

export function formatTime(sec) {
    sec = Math.max(0, Math.round(sec));
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;
    if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    return `${m}:${String(s).padStart(2, "0")}`;
}

function srtTime(sec) {
    const ms = Math.round(sec * 1000);
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    const s = Math.floor((ms % 60000) / 1000);
    const milli = ms % 1000;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")},${String(milli).padStart(3, "0")}`;
}
